import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  InputAdornment,
  Alert,
} from "@mui/material";
import { Email, Lock } from "@mui/icons-material";
import authService from "../services/authService";
import { useDraggable } from "../hooks/useDraggable";
import BannerImage from "../assets/pic2.png";

const LoginForm = () => {
  const navigate = useNavigate();
  const { position, isDragging, handleMouseDown } = useDraggable();

  const [formData, setFormData] = useState({ email: "", password: "" });
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      await authService.login(formData.email, formData.password);
      navigate("/");
      window.location.reload();
    } catch (err) {
      setError("Invalid email or password");
    }
  };

  return (
    <Box sx={styles.container}>
      {/* Blurred Background */}
      <Box sx={styles.blurredBackground} />

      {/* Draggable Card */}
      <Paper
        elevation={12}
        sx={{
          ...styles.card,
          left: position.x,
          top: position.y,
          cursor: isDragging ? "grabbing" : "default",
        }}
        onMouseDown={handleMouseDown}
      >
        {/* Banner / Handle */}
        <Box className="draggable-handle" sx={styles.banner}>
          {/* Invisible Logo Text Placeholder */}
        </Box>

        {/* Form Section */}
        <Box component="form" onSubmit={handleLogin} sx={styles.formSection}>
          <Box sx={{ mb: 1 }}>
            <Typography variant="h4" component="h1" sx={styles.title}>
              Deep Toolbox
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "#666", fontSize: "15px" }}
            >
              Welcome back! Please enter your details.
            </Typography>
          </Box>

          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            required
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Email sx={{ color: "#888" }} />
                </InputAdornment>
              ),
            }}
            sx={styles.input}
          />

          <TextField
            fullWidth
            label="Password"
            name="password"
            type="password"
            value={formData.password}
            onChange={handleChange}
            required
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock sx={{ color: "#888" }} />
                </InputAdornment>
              ),
            }}
            sx={styles.input}
          />

          {error && (
            <Alert severity="error" sx={{ mt: 1, borderRadius: "8px" }}>
              {error}
            </Alert>
          )}

          <Button
            type="submit"
            variant="contained"
            fullWidth
            sx={styles.button}
          >
            Log in
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

const styles = {
  container: {
    width: "100vw",
    height: "100vh",
    position: "relative",
    overflow: "hidden",
  },
  blurredBackground: {
    position: "fixed",
    inset: 0,
    zIndex: 0,
    backgroundImage: `url(${BannerImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    backgroundAttachment: "fixed",
    filter: "blur(8px) brightness(0.9)",
    transform: "scale(1.05)",
  },
  card: {
    display: "flex",
    borderRadius: "12px",
    overflow: "hidden",
    maxWidth: "1000px",
    width: "100%",
    position: "absolute",
    zIndex: 10,
    boxShadow: "0 20px 40px rgba(0,0,0,0.4)",
  },
  banner: {
    minWidth: "400px",
    position: "relative",
    backgroundImage: `url(${BannerImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    backgroundAttachment: "fixed",
    display: { xs: "none", md: "block" },
    cursor: "grab",
    borderRight: "1px solid rgba(255,255,255,0.3)",
    "&:active": { cursor: "grabbing" },
    "&::after": {
      content: '""',
      position: "absolute",
      inset: 0,
      background: "rgba(255,255,255,0.1)",
      pointerEvents: "none",
    },
  },
  formSection: {
    flex: 1,
    padding: { xs: 3, md: 6 },
    display: "flex",
    flexDirection: "column",
    gap: 2.5,
    minWidth: { xs: "100%", md: "450px" },
    backgroundColor: "rgba(255,255,255,0.95)",
    backdropFilter: "blur(10px)",
  },
  title: {
    fontWeight: "800",
    mb: 1,
    color: "#1a1a1a",
    fontFamily: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  },
  input: {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      backgroundColor: "#f9f9f9",
    },
  },
  button: {
    mt: 2,
    py: 1.5,
    borderRadius: "8px",
    backgroundColor: "#222",
    fontSize: "16px",
    fontWeight: 600,
    textTransform: "none",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    "&:hover": {
      backgroundColor: "#000",
      boxShadow: "0 6px 16px rgba(0,0,0,0.2)",
    },
  },
};

export default LoginForm;
